// UUID generation utility for CareerLift application
// Provides UUID v4 generation and validation for user identification

/**
 * Generates a UUID v4 string
 * @returns {string} A valid UUID v4 string
 */
export const generateUUID = () => {
  try {
    // Use crypto.randomUUID() if available (modern browsers)
    if (typeof crypto !== 'undefined' && 
        crypto && 
        typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (cryptoError) {
    console.warn('Crypto API unavailable, using fallback UUID generation');
  }
  
  // Fallback to manual UUID generation with safer replace function
  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  return template?.replace(/[xy]/g, function(c) {
    try {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v?.toString(16);
    } catch (replaceError) {
      // Fallback character in case of any error
      return c === 'x' ? '0' : '8';
    }
  });
};

/**
 * Validates if a string is a valid UUID format
 * @param {string} uuid - String to validate
 * @returns {boolean} True if valid UUID format
 */
export const isValidUUID = (uuid) => {
  try {
    if (typeof uuid !== 'string' || !uuid) return false;
    
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex?.test(uuid);
  } catch (validationError) {
    console.warn('UUID validation error:', validationError);
    return false;
  }
};

/**
 * Converts a numeric ID to a consistent UUID format
 * Useful for migrating existing numeric IDs to UUID format
 * @param {number|string} numericId - Numeric ID to convert
 * @returns {string} A consistent UUID based on the numeric ID
 */
export const numericIdToUUID = (numericId) => {
  try {
    if (!numericId && numericId !== 0) return generateUUID();
    
    // Create a consistent UUID based on the numeric ID
    const paddedId = String(numericId)?.padStart(12, '0');
    const part1 = paddedId?.slice(0, 8);
    const part2 = paddedId?.slice(8, 12);
    return `${part1}-${part2}-4000-8000-000000000000`;
  } catch (conversionError) {
    console.warn('Numeric ID to UUID conversion error:', conversionError);
    return generateUUID();
  }
};

/**
 * Ensures an ID is in UUID format
 * Converts numeric IDs to UUID format, validates existing UUIDs
 * @param {string|number} id - ID to ensure is UUID format
 * @returns {string} A valid UUID string
 */
export const ensureUUID = (id) => {
  try {
    if (!id && id !== 0) return generateUUID();
    
    const idString = String(id);
    
    // If already a valid UUID, return as-is
    if (isValidUUID(idString)) {
      return idString;
    }
    
    // If numeric, convert to consistent UUID
    const numericRegex = /^\d+$/;
    if (numericRegex?.test(idString)) {
      return numericIdToUUID(idString);
    }
    
    // If neither, generate new UUID
    return generateUUID();
  } catch (ensureError) {
    console.warn('Ensure UUID error:', ensureError);
    return generateUUID();
  }
};

export default {
  generateUUID,
  isValidUUID,
  numericIdToUUID,
  ensureUUID
};